/**
 * Navegación por pestañas de las vistas de sesión (criterios 26.4, 26.6, 26.7).
 *
 * Dibuja lo que `lib/navegacion.ts` declara: la fila de siete pestañas y, dentro de la activa, las
 * vistas de ese dominio. El criterio con que los grupos se armaron está en la cabecera de ese módulo;
 * aquí solo está cómo se presenta.
 *
 * **Dos niveles y no uno.** Un solo nivel con las veinte vistas envolvería en seis líneas a 320 px y
 * empujaría la vista fuera de la pantalla. Con dos, lo que siempre se ve son siete rótulos cortos, y
 * el segundo nivel aparece únicamente para el grupo en el que se está: entre cuatro y nueve enlaces en
 * pantalla en lugar de veinte, y ninguna vista deja de ser alcanzable con el ratón o el teclado.
 *
 * **Cada nivel es su propio `<nav>` con su `aria-label`.** Son dos conjuntos distintos —las secciones
 * del producto y las vistas de una sección—, y un lector de pantalla los ofrece por separado en su
 * lista de regiones: «Secciones» y «Vistas de Alimentación». Un solo `<nav>` con los dos niveles
 * dentro se anunciaría como una lista de veintisiete enlaces sin decir dónde termina uno y empieza el
 * otro.
 *
 * **La pestaña activa se marca con `aria-current="page"`, y eso es lo que la marca de verdad.** El
 * color y el borde son la señal para quien ve la pantalla; el atributo es la que reciben el lector de
 * pantalla y el dictado por voz. Sin él, la única diferencia entre la pestaña en la que se está y las
 * otras seis sería un tono, que es exactamente lo que el criterio 26.6 no admite como única señal. Se
 * marca además el enlace de la vista dentro del segundo nivel: es otro conjunto, así que tiene su
 * propio elemento actual.
 *
 * **Son enlaces y no botones.** Llevan a otra pantalla, así que entran en el historial, se abren en
 * otra pestaña con el gesto habitual y se pueden copiar. El foco visible es el de `:focus-visible` de
 * la hoja y aquí no se anula ni se reemplaza (criterio 26.7).
 *
 * **Listas de verdad.** Las pestañas son un conjunto de enlaces hermanos, así que van en un `<ul>`:
 * con eso un lector de pantalla anuncia cuántas hay y por cuál va. La viñeta se quita en la hoja, no
 * el significado.
 */

import { Link, useLocation } from 'react-router';

import {
  GRUPOS_DE_NAVEGACION,
  destinoDelGrupo,
  posicionEnLaNavegacion,
} from '../lib/navegacion';

/** Rótulo de la región del primer nivel. Se declara para que la prueba lo compare sin copiarlo. */
export const ROTULO_DE_LAS_SECCIONES = 'Secciones';

/**
 * Rótulo de la región del segundo nivel, con el nombre del grupo dentro.
 *
 * Va como función y no como plantilla escrita en el JSX porque el rótulo tiene que decir **de qué**
 * son las vistas: «Vistas» a secas se anunciaría igual en los siete grupos y no distinguiría una
 * región de la otra al recorrerlas.
 *
 * @param grupo rótulo visible de la pestaña activa
 */
export function rotuloDeLasVistas(grupo: string): string {
  return `Vistas de ${grupo}`;
}

export function Navegacion() {
  const { pathname } = useLocation();
  const posicion = posicionEnLaNavegacion(pathname);
  const activo = posicion?.grupo;

  return (
    <div className="aisalud-contenido aisalud-navegacion">
      <nav aria-label={ROTULO_DE_LAS_SECCIONES}>
        <ul className="aisalud-navegacion__fila">
          {GRUPOS_DE_NAVEGACION.map((grupo) => (
            <li key={grupo.id}>
              <Link
                aria-current={grupo.id === activo?.id ? 'page' : undefined}
                className="aisalud-navegacion__pestana"
                to={destinoDelGrupo(grupo)}
              >
                {grupo.etiqueta}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Un grupo de una sola vista no abre el segundo nivel: repetiría el enlace de su propia
          pestaña, y un enlace que lleva a donde ya se está no es una salida sino ruido. */}
      {activo !== undefined && activo.vistas.length > 1 && (
        <nav aria-label={rotuloDeLasVistas(activo.etiqueta)}>
          <ul className="aisalud-navegacion__fila aisalud-navegacion__fila--vistas">
            {activo.vistas.map((vista) => (
              <li key={vista.camino}>
                <Link
                  aria-current={vista.camino === posicion?.vista.camino ? 'page' : undefined}
                  className="aisalud-navegacion__vista"
                  to={vista.camino}
                >
                  {vista.etiqueta}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
